import type { PolicySummary } from '../../lib/api'

/**
 * What is waiting for a person on the Policies screen.
 *
 * The loop this closes has an obvious failure mode: an analyst asks for a policy from a case, a draft
 * is saved, and nobody ever looks at it. The draft is not a policy - it reaches no device and changes
 * nothing - so a queue of unreviewed drafts is a queue of work somebody believes is done.
 *
 * A policy is awaiting review when its newest revision is ahead of its newest approved one. That is
 * derived from the list already on the screen rather than counted by the server, because it is true
 * of the rows in hand and needs no second read to be true.
 *
 * Pure and tested beside the screen (D-186); the banner is markup.
 */

/** The value `policy.policy_revision.source_kind` carries for a revision drafted from a case. */
export const FROM_CASE = 'case'

export interface AwaitingReview {
  /** Policies whose newest revision has never been approved. */
  total: number
  /** How many of those were drafted from a case rather than typed by somebody. */
  fromCases: number
  /** Null when there is nothing to say. The banner renders nothing rather than "0 waiting". */
  message: string | null
}

export function awaitingReview(policies: readonly PolicySummary[] | null): AwaitingReview {
  // A list that has not arrived is not an empty list. Saying "nothing is waiting" because a read
  // failed is the reassuring-wrong direction, so it says nothing at all (D-263).
  if (policies === null) return { total: 0, fromCases: 0, message: null }

  // Retired policies are excluded by name rather than by relying on the list's default filter. A
  // retired policy with an unapproved revision satisfies the arithmetic and is waiting for nobody:
  // it cannot be published, so counting it would put a number in front of an operator that no amount
  // of reviewing could bring down. The screen happens to default to active, which is exactly the sort
  // of thing that makes a defect invisible until somebody changes the filter.
  const waiting = policies.filter(
    (policy) => policy.status !== 'retired' && policy.latestRevision > (policy.approvedRevision ?? 0)
  )
  const fromCases = waiting.filter((policy) => policy.latestSourceKind === FROM_CASE).length

  if (waiting.length === 0) return { total: 0, fromCases: 0, message: null }

  const drafts = `${waiting.length} ${waiting.length === 1 ? 'policy has a draft' : 'policies have drafts'} waiting for review`

  // The case count is the half worth stating. A draft somebody typed is a draft they know about; one
  // produced from a case was asked for by a different person, who is waiting and cannot see why not.
  const because =
    fromCases === 0
      ? ''
      : fromCases === waiting.length
        ? ', all of them drafted from cases'
        : `, ${fromCases} of them drafted from cases`

  return {
    total: waiting.length,
    fromCases,
    message: `${drafts}${because}. A draft reaches no device until somebody approves and publishes it.`
  }
}
